import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ChoiceButton, Pressable, Sheet, Text, type ChoiceState } from '@fluent/ui';
import { EXAM_LETTERS } from '@fluent/types';

/**
 * Building blocks shared by the six exam tasks: the task header, a
 * multiple-choice question, running text with tappable gaps, the option
 * sheet those gaps open, the glossary strip and the review "why" note.
 *
 * `review` mode is the same markup, read-only, coloured right/wrong.
 */

export const LETTERS = EXAM_LETTERS;

export function TaskHeader({ n, children }: { n: number; children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <header className="fluent-exam__task-head">
      <p className="fluent-exam__task-kicker">
        {t('exam.task_label', { n })} · {t(`exam.task_${n}`)}
      </p>
      <p className="fluent-exam__instr" lang="en">
        {t(`exam.instr_en_${n}`)}
      </p>
      <p className="fluent-exam__instr-ka">{t(`exam.instr_ka_${n}`)}</p>
      {children}
    </header>
  );
}

/** Paragraphs are separated by a blank line in the content files. */
export function paragraphsOf(text: string): string[] {
  return text.split(/\n\s*\n/u).map((p) => p.trim()).filter(Boolean);
}

export function Glossary({ items }: { items: readonly { word: string; ka: string }[] }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <p className="fluent-exam__glossary" aria-label={t('exam.glossary')}>
      {items.map((g) => (
        <span key={g.word}>
          * <span lang="en">{g.word}</span>: {g.ka}
        </span>
      ))}
    </p>
  );
}

function stateFor(letter: string, picked: string | undefined, correct: string | undefined, review: boolean): ChoiceState {
  if (!review) return 'idle';
  if (letter === correct) return 'correct';
  if (letter === picked) return 'wrong';
  return 'idle';
}

export interface McqProps {
  number: number;
  question: string;
  options: readonly string[];
  picked: string | undefined;
  onPick: (letter: string) => void;
  review: boolean;
  correct?: string;
  evidence?: string;
}

/** One multiple-choice question. The real paper numbers questions and
 *  letters options; so do we. Tapping the picked option again keeps it. */
export function McqQuestion({ number, question, options, picked, onPick, review, correct, evidence }: McqProps) {
  return (
    <section className="fluent-exam__q" data-review={review || undefined}>
      <p className="fluent-exam__q-text" lang="en">
        <span className="fluent-exam__q-num">{number}</span>
        {question}
      </p>
      <div className="fluent-exam__choices" role="radiogroup" aria-label={question}>
        {options.map((opt, i) => {
          const letter = LETTERS[i] ?? '';
          return (
            <ChoiceButton
              key={letter}
              bullet={letter}
              selected={picked === letter}
              state={stateFor(letter, picked, correct, review)}
              onClick={() => !review && onPick(letter)}
              aria-disabled={review || undefined}
              role="radio"
              aria-checked={picked === letter}
              lang="en"
            >
              {opt}
            </ChoiceButton>
          );
        })}
      </div>
      {review && <ReviewNote picked={picked} correct={correct} evidence={evidence} />}
    </section>
  );
}

/** Under a reviewed item: your answer, the right one, and the proof. */
export function ReviewNote({
  picked,
  correct,
  evidence,
  why,
  label,
}: {
  picked: string | undefined;
  correct: string | undefined;
  evidence?: string;
  why?: string;
  /** Render a letter as something longer (e.g. "G · keeps"). */
  label?: (letter: string) => string;
}) {
  const { t } = useTranslation();
  const show = (l: string | undefined) => (l === undefined ? t('exam.review_none') : label ? label(l) : l);
  const right = picked !== undefined && picked === correct;
  return (
    <div className="fluent-exam__review-note" data-right={right || undefined}>
      {!right && (
        <p>
          {t('exam.review_your', { answer: show(picked) })} · <b>{t('exam.review_correct', { answer: show(correct) })}</b>
        </p>
      )}
      {evidence && (
        <p>
          <span className="fluent-exam__review-label">{t('exam.review_evidence')}</span>{' '}
          <q lang="en">{evidence}</q>
        </p>
      )}
      {why && <p>{why}</p>}
    </div>
  );
}

export interface GapTextProps {
  text: string;
  /** What to print inside gap n (1-based); undefined = empty. */
  fill: (n: number) => string | undefined;
  onGap: (n: number) => void;
  /** Review colouring per gap. */
  gapState?: (n: number) => 'right' | 'wrong' | undefined;
  /** Text in an empty gap — "choose" while answering, a dash in review. */
  placeholder?: string;
}

/** Running text where every `{{n}}` becomes a tappable gap. */
export function GapText({ text, fill, onGap, gapState, placeholder }: GapTextProps) {
  const { t } = useTranslation();
  return (
    <div className="fluent-exam__passage" lang="en">
      {paragraphsOf(text).map((para, pi) => (
        <p key={pi}>
          {para.split(/(\{\{\d+\}\})/u).map((chunk, ci) => {
            const m = /^\{\{(\d+)\}\}$/u.exec(chunk);
            if (!m) return <Fragment key={ci}>{chunk}</Fragment>;
            const n = Number(m[1]);
            const value = fill(n);
            return (
              <Pressable
                key={ci}
                className="fluent-exam__gap"
                data-filled={value !== undefined || undefined}
                data-state={gapState?.(n)}
                onClick={() => onGap(n)}
                aria-label={`${t('exam.gap_aria', { n })}${value ? `: ${value}` : ''}`}
              >
                <span className="fluent-exam__gap-num">{n}</span>
                <span className="fluent-exam__gap-value">{value ?? placeholder ?? t('exam.gap_empty')}</span>
              </Pressable>
            );
          })}
        </p>
      ))}
    </div>
  );
}

export interface OptionSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gap: number;
  options: readonly string[];
  picked: string | undefined;
  onPick: (letter: string) => void;
  onClear: () => void;
  /** For shared-pool tasks: letter → the gap that already holds it. */
  usedIn?: (letter: string) => number | undefined;
}

/** Bottom sheet listing the choices for one gap. */
export function OptionSheet({ open, onOpenChange, gap, options, picked, onPick, onClear, usedIn }: OptionSheetProps) {
  const { t } = useTranslation();
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={t('exam.sheet_gap', { n: gap })}
      actions={
        picked !== undefined ? (
          <Button variant="ghost" size="md" fullWidth onClick={onClear}>
            {t('exam.sheet_clear')}
          </Button>
        ) : undefined
      }
    >
      <div className="fluent-exam__sheet-list">
        {options.map((opt, i) => {
          const letter = LETTERS[i] ?? '';
          const elsewhere = usedIn?.(letter);
          return (
            <ChoiceButton
              key={letter}
              bullet={letter}
              selected={picked === letter}
              onClick={() => onPick(letter)}
              lang="en"
              data-used={elsewhere !== undefined && elsewhere !== gap ? true : undefined}
            >
              <span className="fluent-exam__sheet-opt">
                {opt}
                {elsewhere !== undefined && elsewhere !== gap && (
                  <Text as="span" size="xs" tone="muted" className="fluent-exam__sheet-used">
                    {t('exam.sheet_used_in', { n: elsewhere })}
                  </Text>
                )}
              </span>
            </ChoiceButton>
          );
        })}
      </div>
    </Sheet>
  );
}
